import { useState, type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useFormikContext, getIn } from "formik";
import moment, { type Moment } from "moment-timezone";

import { type IncidentReportIndividualSupportSessionParticipant, type IncidentReportIndividualSupportSessionFollowUpSession } from "components/CriticalIncident/interfaces";
import { FormControlWrapper } from "components/Shared";
import { TextBox, DateInput, Select, Button } from "components/Shared/Inputs";
import { confirm } from "components/Shared/ConfirmDialog";
import { ReportSectionAccordion } from "components/CriticalIncident/components/Shared/ReportSectionAccordion";
import { ChooseYesNo } from "components/CriticalIncident/components/ChooseYesNo";
import { EMPTY_FOLLOW_UP_SESSION, FOLLOW_UP_SESSION_TYPES, MAX_FOLLOW_UP_SESSIONS, MAX_CHARS } from "../../IndividualSupportSession.constants";

export interface FollowUpSessionsProps {
  basePath: string;
  participant: IncidentReportIndividualSupportSessionParticipant;
  isReadonly: boolean;
  patchParticipant: (partial: Partial<IncidentReportIndividualSupportSessionParticipant>) => void;
}

// AU-only (TPH-1909)
export const FollowUpSessions: FunctionComponent<FollowUpSessionsProps> = ({ basePath, participant, isReadonly, patchParticipant }) => {
  const { t } = useTranslation("criticalIncident");
  const { touched, errors, setFieldValue } = useFormikContext();
  const [expandedSessionIndex, setExpandedSessionIndex] = useState<number | false>(-1);
  const sessions = participant.followUpSessions ?? [];

  const gateTouched = getIn(touched, `${basePath}.wereThereAnyFollowUpSessions`) as boolean | undefined;
  const gateErrors = getIn(errors, `${basePath}.wereThereAnyFollowUpSessions`) as string | undefined;
  const sessionsTouched = getIn(touched, `${basePath}.followUpSessions`) as Record<string, unknown>[] | undefined;
  const sessionsErrors = getIn(errors, `${basePath}.followUpSessions`) as Record<string, string>[] | undefined;

  const handleGateChange = async (wereThereAnyFollowUpSessions: boolean) => {
    if (!wereThereAnyFollowUpSessions && participant.wereThereAnyFollowUpSessions) {
      const confirmed = await confirm({
        title: t("clear_followup_sessions_confirm_title"),
        message: t("clear_followup_sessions_confirm_text"),
        confirmLabel: t("yes_remove"),
      });
      if (!confirmed) return;
    }

    const nextSessions = wereThereAnyFollowUpSessions ? (sessions.length ? sessions : [{ ...EMPTY_FOLLOW_UP_SESSION }]) : [];
    const noOfFollowUpSessions = nextSessions.length;

    setFieldValue(`${basePath}.wereThereAnyFollowUpSessions`, wereThereAnyFollowUpSessions);
    setFieldValue(`${basePath}.noOfFollowUpSessions`, noOfFollowUpSessions);
    setFieldValue(`${basePath}.followUpSessions`, nextSessions);
    patchParticipant({ wereThereAnyFollowUpSessions, noOfFollowUpSessions, followUpSessions: nextSessions });
  };

  const handleAddSession = () => {
    if (sessions.length >= MAX_FOLLOW_UP_SESSIONS) return;
    const nextSessions = [...sessions, { ...EMPTY_FOLLOW_UP_SESSION }];
    setFieldValue(`${basePath}.followUpSessions`, nextSessions);
    setFieldValue(`${basePath}.noOfFollowUpSessions`, nextSessions.length);
    patchParticipant({ followUpSessions: nextSessions, noOfFollowUpSessions: nextSessions.length });
    setExpandedSessionIndex(nextSessions.length - 1);
  };

  const handleRemoveSession = (index: number) => {
    const nextSessions = sessions.filter((_, i) => i !== index);
    setFieldValue(`${basePath}.followUpSessions`, nextSessions);
    setFieldValue(`${basePath}.noOfFollowUpSessions`, nextSessions.length);
    patchParticipant({ followUpSessions: nextSessions, noOfFollowUpSessions: nextSessions.length });
  };

  const updateSessionField = (index: number, field: keyof IncidentReportIndividualSupportSessionFollowUpSession, value: unknown) => {
    const nextSessions = sessions.map((session, i) => (i === index ? { ...session, [field]: value } : session));
    setFieldValue(`${basePath}.followUpSessions[${index}].${field}`, value);
    patchParticipant({ followUpSessions: nextSessions });
  };

  const makeChangeHandler = (index: number, field: keyof IncidentReportIndividualSupportSessionFollowUpSession) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateSessionField(index, field, event.target.value);

  const handleSessionDateChange = (index: number) => (value: Moment | null) => {
    updateSessionField(index, "sessionDate", value?.startOf("day")?.toDate().toISOString() || null);
  };

  return (
    <Grid item sm={12}>
      <FormControlWrapper
        isReadonly={isReadonly}
        type="radio.yesno"
        label={`${t("did_you_follow_up_with_the_contact")}*`}
        name={`${basePath}.wereThereAnyFollowUpSessions`}
        fullWidth
        error={Boolean(gateTouched) && Boolean(gateErrors)}
        helperText={gateTouched ? t(gateErrors || " ") : ""}
      >
        <ChooseYesNo
          label={`${t("did_you_follow_up_with_the_contact")}*`}
          name={`${basePath}.wereThereAnyFollowUpSessions`}
          value={participant.wereThereAnyFollowUpSessions}
          onChange={(value) => handleGateChange(value as boolean)}
        />
      </FormControlWrapper>

      {participant.wereThereAnyFollowUpSessions && (
        <Grid container spacing={2}>
          {sessions.map((session, index) => {
            const sessionTouched = sessionsTouched?.[index] as Record<string, unknown> | undefined;
            const sessionErrors = sessionsErrors?.[index] as Record<string, string> | undefined;

            return (
              <Grid item sm={12} key={index}>
                <ReportSectionAccordion
                  label={t("session_number", { number: index + 1 })}
                  name={`${basePath}.followUpSessions[${index}]`}
                  pristineValue={EMPTY_FOLLOW_UP_SESSION}
                  removeAction={isReadonly ? undefined : () => handleRemoveSession(index)}
                  removeHidden={sessions.length <= 1}
                  expanded={expandedSessionIndex === index}
                  onToggle={(isExpanded) => setExpandedSessionIndex(isExpanded ? index : false)}
                  readonly={isReadonly}
                >
                  <Grid container spacing={2}>
                    <Grid item sm={6}>
                      <FormControlWrapper
                        isReadonly={isReadonly}
                        type="date"
                        dateFormat="L"
                        label={t("follow_up_session_date")}
                        name={`${basePath}.followUpSessions[${index}].sessionDate`}
                      >
                        <DateInput
                          label={t("follow_up_session_date")}
                          required
                          name={`${basePath}.followUpSessions[${index}].sessionDate`}
                          onChange={handleSessionDateChange(index)}
                          value={session.sessionDate ? moment(session.sessionDate) : null}
                          error={Boolean(sessionTouched?.sessionDate) && Boolean(sessionErrors?.sessionDate)}
                          helperText={sessionTouched?.sessionDate ? t(sessionErrors?.sessionDate || " ") : ""}
                          fullWidth
                        />
                      </FormControlWrapper>
                    </Grid>
                    <Grid item sm={6}>
                      <FormControlWrapper
                        isReadonly={isReadonly}
                        type="select"
                        fullWidth
                        label={`${t("follow_up_session_type")}*`}
                        name={`${basePath}.followUpSessions[${index}].sessionType`}
                        required
                        options={FOLLOW_UP_SESSION_TYPES.map((option) => ({ value: option.value, label: t(option.labelKey) }))}
                        error={Boolean(sessionTouched?.sessionType) && Boolean(sessionErrors?.sessionType)}
                        helperText={sessionTouched?.sessionType ? t(sessionErrors?.sessionType || " ") : ""}
                      >
                        <Select
                          name={`${basePath}.followUpSessions[${index}].sessionType`}
                          value={session.sessionType ?? ""}
                          onChange={(event) => updateSessionField(index, "sessionType", event.target.value as string)}
                        >
                          {FOLLOW_UP_SESSION_TYPES.map((option) => (
                            <MenuItem value={option.value} key={option.value}>{t(option.labelKey)}</MenuItem>
                          ))}
                        </Select>
                      </FormControlWrapper>
                    </Grid>
                    <Grid item sm={12}>
                      <FormControlWrapper
                        isReadonly={isReadonly}
                        label={t("presentation")}
                        name={`${basePath}.followUpSessions[${index}].presentation`}
                        fullWidth
                      >
                        <TextBox
                          multiline
                          minRows={4}
                          maxRows={6}
                          label={t("presentation")}
                          required
                          name={`${basePath}.followUpSessions[${index}].presentation`}
                          value={session.presentation}
                          onChange={makeChangeHandler(index, "presentation")}
                          slotProps={{ input: { maxLength: MAX_CHARS } }}
                          error={Boolean(sessionTouched?.presentation) && Boolean(sessionErrors?.presentation)}
                          helperText={sessionTouched?.presentation ? t(sessionErrors?.presentation || " ") : ""}
                        />
                      </FormControlWrapper>
                    </Grid>
                    <Grid item sm={12}>
                      <FormControlWrapper
                        isReadonly={isReadonly}
                        label={t("follow_up_risk")}
                        name={`${basePath}.followUpSessions[${index}].risk`}
                        fullWidth
                      >
                        <TextBox
                          multiline
                          minRows={4}
                          maxRows={6}
                          label={t("follow_up_risk")}
                          required
                          name={`${basePath}.followUpSessions[${index}].risk`}
                          value={session.risk}
                          onChange={makeChangeHandler(index, "risk")}
                          slotProps={{ input: { maxLength: MAX_CHARS } }}
                          error={Boolean(sessionTouched?.risk) && Boolean(sessionErrors?.risk)}
                          helperText={sessionTouched?.risk ? t(sessionErrors?.risk || " ") : ""}
                        />
                      </FormControlWrapper>
                    </Grid>
                    <Grid item sm={12}>
                      <FormControlWrapper
                        isReadonly={isReadonly}
                        label={t("follow_up_intervention")}
                        name={`${basePath}.followUpSessions[${index}].intervention`}
                        fullWidth
                      >
                        <TextBox
                          multiline
                          minRows={4}
                          maxRows={6}
                          label={t("follow_up_intervention")}
                          required
                          name={`${basePath}.followUpSessions[${index}].intervention`}
                          value={session.intervention}
                          onChange={makeChangeHandler(index, "intervention")}
                          slotProps={{ input: { maxLength: MAX_CHARS } }}
                          error={Boolean(sessionTouched?.intervention) && Boolean(sessionErrors?.intervention)}
                          helperText={sessionTouched?.intervention ? t(sessionErrors?.intervention || " ") : ""}
                        />
                      </FormControlWrapper>
                    </Grid>
                    <Grid item sm={12}>
                      <FormControlWrapper
                        isReadonly={isReadonly}
                        label={t("follow_up_recommendation")}
                        name={`${basePath}.followUpSessions[${index}].recommendation`}
                        fullWidth
                      >
                        <TextBox
                          multiline
                          minRows={4}
                          maxRows={6}
                          label={t("follow_up_recommendation")}
                          required
                          name={`${basePath}.followUpSessions[${index}].recommendation`}
                          value={session.recommendation}
                          onChange={makeChangeHandler(index, "recommendation")}
                          slotProps={{ input: { maxLength: MAX_CHARS } }}
                          error={Boolean(sessionTouched?.recommendation) && Boolean(sessionErrors?.recommendation)}
                          helperText={sessionTouched?.recommendation ? t(sessionErrors?.recommendation || " ") : ""}
                        />
                      </FormControlWrapper>
                    </Grid>
                  </Grid>
                </ReportSectionAccordion>
              </Grid>
            );
          })}

          {!isReadonly && sessions.length < MAX_FOLLOW_UP_SESSIONS && (
            <Grid item sm={12}>
              <Button variant="text" onClick={handleAddSession}>
                <AddIcon fontSize="small" />
                {t("add_follow_up_session")}
              </Button>
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  );
};
